import type { NoteServiceDefinitionOption } from '@/db/types/case_note';
import type { CaseNoteFormState } from '@/lib/case-note-form';
import { inputClass } from './fields';

interface ServiceUnitsFieldProps {
  definitions: NoteServiceDefinitionOption[];
  units: CaseNoteFormState['units'];
  onChange: (units: CaseNoteFormState['units']) => void;
  error?: string;
}

/** Units logged on a case note: a count and a funding source for each of the program's services. */
export default function ServiceUnitsField({ definitions, units, onChange, error }: ServiceUnitsFieldProps) {
  if (definitions.length === 0) return null;

  const update = (definition: NoteServiceDefinitionOption, patch: Partial<{ count: string; fundingSourceId: number | null }>) => {
    const current = units[definition.id] ?? { count: '', fundingSourceId: definition.default_funding_source_id };
    onChange({ ...units, [definition.id]: { ...current, ...patch } });
  };

  return (
    <fieldset className="space-y-2">
      <legend className="block text-sm font-medium text-gray-700 mb-1">Service units</legend>
      <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 bg-white">
        {definitions.map(definition => {
          const entry = units[definition.id];
          const fundingSourceId = entry?.fundingSourceId ?? definition.default_funding_source_id;
          const countId = `unit-count-${definition.id}`;
          const fundingId = `unit-funding-${definition.id}`;
          return (
            <div key={definition.id} className="p-3 space-y-2" data-testid="service-unit">
              <div className="flex items-center justify-between gap-3">
                <label htmlFor={countId} className="text-sm font-semibold text-gray-900">{definition.name}</label>
                <div className="flex items-center gap-2 shrink-0">
                  <input
                    id={countId}
                    type="number"
                    inputMode={definition.accepts_partial_units ? 'decimal' : 'numeric'}
                    min={0}
                    step={definition.accepts_partial_units ? 0.25 : 1}
                    value={entry?.count ?? ''}
                    placeholder="0"
                    onChange={e => update(definition, { count: e.target.value })}
                    className={`${inputClass()} w-24 text-right`}
                  />
                  <span className="text-sm text-gray-600 w-14">{definition.unit_name}s</span>
                </div>
              </div>
              {definition.funding_sources.length > 0 && (
                <div className="flex items-center gap-2">
                  <label htmlFor={fundingId} className="text-xs text-gray-500 shrink-0">Funding</label>
                  <select
                    id={fundingId}
                    value={fundingSourceId ?? ''}
                    onChange={e => update(definition, { fundingSourceId: e.target.value ? Number(e.target.value) : null })}
                    className={`${inputClass()} py-2`}
                  >
                    {definition.funding_sources.map(source => (
                      <option key={source.id} value={source.id}>{source.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {error && <p className="text-xs text-red-600">{error}</p>}
    </fieldset>
  );
}
